/**
 * Форматирование чисел и разметка подсказок графиков.
 *
 * Подписи рядов и категорий приходят из данных (названия тем, кабинетов, артикулы), поэтому в HTML подсказки
 * они попадают только через `escapeHtml`. Значение в подсказке — главный, крупный элемент; подпись — мельче
 * (правило dataviz «values lead, labels follow»). Ряд обозначается короткой линией его цвета, а не квадратом.
 */

import type { ChartTheme } from './palette'

const NBSP = ' '
const MINUS = '−'
const DASH = '—'

const formats = new Map<number, Intl.NumberFormat>()
function numberFormat(digits: number): Intl.NumberFormat {
  let format = formats.get(digits)
  if (!format) {
    format = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: digits })
    formats.set(digits, format)
  }
  return format
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/** «1 234» (пусто — «—»). */
export function formatNumber(value: number | null | undefined, digits = 0): string {
  return isFiniteNumber(value) ? numberFormat(digits).format(value) : DASH
}

/** Короткая запись для осей и ячеек: 1 234 → «1,2 тыс.», 2 500 000 → «2,5 млн». */
export function formatCompactNumber(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) return `${numberFormat(1).format(value / 1_000_000_000)}${NBSP}млрд`
  if (abs >= 1_000_000) return `${numberFormat(1).format(value / 1_000_000)}${NBSP}млн`
  if (abs >= 10_000) return `${numberFormat(0).format(value / 1_000)}${NBSP}тыс.`
  if (abs >= 1_000) return `${numberFormat(1).format(value / 1_000)}${NBSP}тыс.`
  return numberFormat(abs < 10 && abs % 1 !== 0 ? 1 : 0).format(value)
}

/** Доля 0..1 → «12,4%». */
export function formatShare(value: number | null | undefined, digits = 1): string {
  if (!isFiniteNumber(value)) return DASH
  return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value * 100)}%`
}

/** Относительное изменение: (текущее − прежнее) / |прежнее|; нечего сравнивать — `null`. */
export function relativeDelta(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (!isFiniteNumber(current) || !isFiniteNumber(previous) || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

/** Изменение-доля → «+12,4%» / «−3,1%»; ноль без знака; пусто — «—». */
export function formatSignedShare(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH
  const rounded = Math.abs(value) < 0.0005 ? 0 : value
  const text = formatShare(Math.abs(rounded))
  return rounded > 0 ? `+${text}` : rounded < 0 ? `${MINUS}${text}` : text
}

export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// ── Подсказки ──────────────────────────────────────────────────────────────────

export interface TooltipRow {
  /** Цвет ряда; линия-ключ слева. */
  color?: string
  /** Пунктир — ряд сравнения. */
  dashed?: boolean
  label: string
  value: string
  /** Дополнение мелким текстом справа (Δ, доля). */
  note?: string
  /** Цвет дополнения (рост/падение); по умолчанию вторичный текст. */
  noteColor?: string
}

/** Стиль контейнера подсказки ECharts в токенах DS. */
export function tooltipBase(theme: ChartTheme) {
  return {
    backgroundColor: theme.tooltipBackground,
    borderColor: theme.tooltipBorder,
    borderWidth: 1,
    padding: [8, 10] as [number, number],
    textStyle: { color: theme.text, fontFamily: theme.fontFamily, fontSize: 12 },
    extraCssText: 'box-shadow: 0 4px 16px rgba(0,0,0,0.12); border-radius: 8px;',
    confine: true,
  }
}

function lineKey(color: string, dashed?: boolean): string {
  const style = dashed
    ? `border-top:2px dashed ${color};height:0;opacity:.6`
    : `background:${color};height:2px`
  return `<span style="display:inline-block;width:12px;margin-right:6px;vertical-align:middle;${style}"></span>`
}

/** HTML подсказки: заголовок (дата/объект) и строки «значение — подпись». */
export function tooltipHtml(theme: ChartTheme, title: string, rows: TooltipRow[], footer?: string[]): string {
  const head = `<div style="font-size:11px;color:${theme.textMuted};margin-bottom:4px">${escapeHtml(title)}</div>`
  const body = rows
    .map((row) => {
      const key = row.color ? lineKey(row.color, row.dashed) : ''
      const note = row.note
        ? `<span style="margin-left:6px;font-size:11px;color:${row.noteColor ?? theme.textSecondary}">${escapeHtml(row.note)}</span>`
        : ''
      return (
        `<div style="display:flex;align-items:center;gap:2px;line-height:20px;white-space:nowrap">` +
        `${key}<span style="font-weight:600;color:${theme.text};font-variant-numeric:tabular-nums">${escapeHtml(row.value)}</span>` +
        `<span style="margin-left:6px;font-size:11px;color:${theme.textSecondary}">${escapeHtml(row.label)}</span>${note}</div>`
      )
    })
    .join('')
  const tail = (footer ?? [])
    .filter(Boolean)
    .map(
      (line) =>
        `<div style="margin-top:4px;padding-top:4px;border-top:1px solid ${theme.tooltipBorder};font-size:11px;color:${theme.textSecondary};white-space:normal;max-width:260px">${escapeHtml(line)}</div>`,
    )
    .join('')
  return head + body + tail
}

/** Цвет текста Δ: рост/падение; `invert` — для метрик, где рост плох (возвраты, отмены). */
export function deltaColor(theme: ChartTheme, delta: number | null | undefined, invert = false): string | undefined {
  if (!isFiniteNumber(delta) || Math.abs(delta) < 0.0005) return undefined
  const good = invert ? delta < 0 : delta > 0
  return good ? theme.positiveText : theme.negativeText
}

/** Общие подписи осей: приглушённый текст, без линии-«рамки», сплошная тонкая сетка. */
export function axisTextStyle(theme: ChartTheme) {
  return { color: theme.textMuted, fontFamily: theme.fontFamily, fontSize: 11 }
}

/** Текст для rich-подписей ECharts: фигурные скобки и «|» в названиях ломают разметку `{стиль|текст}`. */
export function richSafe(value: unknown): string {
  return String(value ?? '').replace(/[{}|]/g, ' ')
}
